import React, { useState } from 'react';
import { Transaction, Category } from '@/lib/api';
import { translateTransactionType } from '@/lib/translations';
import { formatCurrency, formatDateShort, formatDateTime, toDateString } from '@/lib/format';
import { ArrowDownLeft, ArrowUpRight, CalendarIcon, ChevronDown, Pencil, Trash2, Loader2, X, Check, AlertTriangle, Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getCategoryColorHsl } from '@/lib/utils/categoryColor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { format, parseISO } from 'date-fns';
import { useUpdateTransaction, useDeleteTransaction } from '@/hooks/useTransactions';

interface TransactionItemProps {
  transaction: Transaction;
  category?: Category;
  categories?: Category[];
}

export const TransactionItem: React.FC<TransactionItemProps> = ({
  transaction,
  category,
  categories = [],
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const updateTransaction = useUpdateTransaction();
  const deleteTransaction = useDeleteTransaction();

  const [editForm, setEditForm] = useState({
    amount: transaction.amount,
    category_id: transaction.category_id,
    date: transaction.date,
    description: transaction.description,
    type: transaction.type,
  });

  const isIncome = transaction.type === 'income';
  const filteredCategories = categories.filter((c) => c.type === editForm.type);
  const categoryColorHsl = category
    ? getCategoryColorHsl(category.id, category.type)
    : null;

  const handleDelete = () => {
    deleteTransaction.mutate(transaction.id);
  };

  const handleUpdate = () => {
    updateTransaction.mutate(
      {
        id: transaction.id,
        amount: parseFloat(editForm.amount),
        category_id: editForm.category_id,
        date: editForm.date,
        description: editForm.description,
        type: editForm.type,
      },
      { onSuccess: () => setIsEditing(false) }
    );
  };

  const handleCancelEdit = () => {
    setEditForm({
      amount: transaction.amount,
      category_id: transaction.category_id,
      date: transaction.date,
      description: transaction.description,
      type: transaction.type,
    });
    setIsEditing(false);
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between py-3.5 cursor-pointer hover:bg-muted/20 transition-colors rounded-lg px-2 -mx-2 group gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div
              className={cn(
                'p-1.5 rounded-lg shrink-0',
                !categoryColorHsl && (isIncome ? 'bg-income/12' : 'bg-expense/12'),
              )}
              style={
                categoryColorHsl
                  ? { backgroundColor: `hsl(${categoryColorHsl} / 0.14)` }
                  : undefined
              }
            >
              {isIncome
                ? <ArrowDownLeft className="h-4 w-4 text-income" />
                : <ArrowUpRight className="h-4 w-4 text-expense" />}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground leading-tight truncate">{transaction.description}</p>
              <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 min-w-0">
                <span className="truncate">
                  {category?.name || 'Sem categoria'} · {formatDateShort(transaction.date)}
                </span>
                {transaction.external_id && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        role="img"
                        aria-label="Conciliada"
                        className="inline-flex shrink-0 text-muted-foreground/80"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Link2 className="h-3 w-3" aria-hidden="true" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">Conciliada</TooltipContent>
                  </Tooltip>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <p className={cn(
              'text-sm font-semibold tabular-nums',
              isIncome ? 'text-income' : 'text-expense'
            )}>
              {isIncome ? '+' : '−'}{formatCurrency(transaction.amount)}
            </p>
            <ChevronDown className={cn(
              'h-3.5 w-3.5 text-muted-foreground/50 transition-transform duration-200',
              isExpanded && 'rotate-180'
            )} />
          </div>
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="px-2 pb-3">
          {isEditing ? (
            /* Edit form */
            <div className="bg-muted/30 rounded-lg p-4 border border-border/60">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Tipo</Label>
                  <Select
                    value={editForm.type}
                    onValueChange={(v: 'income' | 'expense') =>
                      setEditForm({ ...editForm, type: v, category_id: '' })
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="income">Receita</SelectItem>
                      <SelectItem value="expense">Despesa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Valor</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.amount}
                    onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                    className="h-9 text-sm"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Categoria</Label>
                  <Select
                    value={editForm.category_id}
                    onValueChange={(v) => setEditForm({ ...editForm, category_id: v })}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id} className="text-sm">{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Data</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-9 w-full justify-start text-left text-sm font-normal"
                      >
                        <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                        {editForm.date ? format(parseISO(editForm.date), 'dd/MM/yyyy') : 'Selecione a data'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={editForm.date ? parseISO(editForm.date) : undefined}
                        onSelect={(date) => date && setEditForm({ ...editForm, date: toDateString(date) })}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs text-muted-foreground">Descrição</Label>
                  <Input
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    className="h-9 text-sm"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button variant="ghost" size="sm" onClick={handleCancelEdit} disabled={updateTransaction.isPending} className="h-8 text-xs">
                  <X className="h-3.5 w-3.5 mr-1" />
                  Cancelar
                </Button>
                <Button size="sm" onClick={handleUpdate} disabled={updateTransaction.isPending} className="h-8 text-xs bg-primary text-primary-foreground border-0">
                  {updateTransaction.isPending
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Salvando...</>
                    : <><Check className="h-3.5 w-3.5 mr-1" /> Salvar</>}
                </Button>
              </div>
            </div>
          ) : (
            /* Detail view */
            <div className="bg-muted/20 rounded-lg p-4 border border-border/40">
              <div className="grid gap-3 sm:grid-cols-3 text-sm mb-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Tipo</p>
                  <p className={cn('font-medium', isIncome ? 'text-income' : 'text-expense')}>
                    {translateTransactionType(transaction.type)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Valor</p>
                  <p className="font-medium tabular-nums">{formatCurrency(transaction.amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Categoria</p>
                  <p className="font-medium">{category?.name || 'Sem categoria'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Data</p>
                  <p className="font-medium">{formatDateShort(transaction.date)}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground mb-0.5">Descrição</p>
                  <p className="font-medium">{transaction.description}</p>
                </div>
                <div className="sm:col-span-3">
                  <p className="text-xs text-muted-foreground mb-0.5">Origem</p>
                  {transaction.external_id ? (
                    <p className="font-medium flex items-center gap-1.5">
                      <Link2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                      Conciliada · banco
                    </p>
                  ) : (
                    <p className="font-medium text-muted-foreground/90">Adicionada manualmente</p>
                  )}
                </div>
                <div className="sm:col-span-3">
                  <p className="text-xs text-muted-foreground mb-0.5">Registrado em</p>
                  <p className="text-xs text-muted-foreground">{formatDateTime(transaction.created_at)}</p>
                </div>
              </div>

              {/* Inline delete confirmation — no dialog */}
              {showDeleteConfirm ? (
                <div className="flex items-center gap-3 p-3 rounded-md bg-destructive/[0.08] border border-destructive/20 animate-fade-in">
                  <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                  <p className="text-xs text-destructive flex-1">Esta ação não pode ser desfeita. Confirma a exclusão?</p>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleteTransaction.isPending}
                    className="text-xs text-destructive-foreground bg-destructive hover:bg-destructive/90 px-3 py-1 rounded font-medium transition-all disabled:opacity-60 flex items-center gap-1"
                  >
                    {deleteTransaction.isPending
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : null}
                    Excluir
                  </button>
                </div>
              ) : (
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-muted-foreground border border-border hover:text-primary hover:border-primary/40 transition-all"
                  >
                    <Pencil className="h-3 w-3" />
                    Editar
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-muted-foreground border border-border hover:text-destructive hover:border-destructive/40 transition-all"
                  >
                    <Trash2 className="h-3 w-3" />
                    Excluir
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
